import { httpClient } from "./client";
import type { ComplianceApi } from "./interfaces";
import type { UserDataExport, PendingUserDeletionResponse } from "../types";

/**
 * Compliance / CCPA self-serve client — the three account privacy endpoints.
 *
 * The backend compliance router (`backend/app/routers/compliance.py`) serves
 * under the `/users` prefix (mirroring the consent router), so its routes live
 * at `/users/me/...`. httpClient prefixes every path with `/bff/api`, so the
 * BFF sees `/bff/api/users/me/...` — the canonical path that resolves directly
 * (no reliance on the BFF's `/me/ → /users/me/` rewrite quirk).
 *
 *   getMyDataExport()              → GET    /users/me/data-export   → UserDataExport
 *   requestAccountDeletion(email)  → DELETE /users/me  (body {confirmation_email})
 *                                                → PendingUserDeletionResponse
 *   getDeletionStatus()            → GET    /users/me/deletion-status
 *                                                → PendingUserDeletionResponse | null
 *
 * DELETE /users/me REQUIRES a `confirmation_email` body that must match the
 * caller's email (anti-session-hijack — the router rejects a mismatch). So
 * requestAccountDeletion forwards the confirmation email in the request body;
 * it is NOT a no-arg call.
 */
export const complianceApi: ComplianceApi = {
  getMyDataExport: () =>
    httpClient.get<UserDataExport>("/users/me/data-export"),

  requestAccountDeletion: (confirmationEmail: string) =>
    httpClient.delete<PendingUserDeletionResponse>("/users/me", {
      body: { confirmation_email: confirmationEmail },
    }),

  getDeletionStatus: () =>
    httpClient.get<PendingUserDeletionResponse | null>(
      "/users/me/deletion-status",
    ),
};
